import {
  DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS,
  DATASET_FOR_UI_CONFIG_REFRESH_FOLLOW_UP,
  DATASET_FOR_UI_CONFIG_REFRESH_ON_REAL_TIME_OPTIMIZE,
  getSandboxProjectUiConfigRefreshToken,
  reFetchAllDatasetsOnElectricityPriceScenario,
  reFetchAllDatasetsOnFuelPlan,
  reFetchAllDatasetsOnOptimize,
} from 'api/dataset/dataset.api'
import { GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY } from 'api/digitalTwin/digitalTwin.api'
import { ELPLAN_BID_MATRIX_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.api'
import {
  FAILED_OPT_JOB_STATUSES,
  OPT_JOBS_AND_QUEUE_ITEMS_STATUS_QUERY_KEY,
  OPT_JOBS_QUERY_KEY,
  OptJobStatus,
  OptJobType,
} from 'api/optJobs/optJobs.api'
import { OPT_PROJECTS_QUERY_KEY, setOptProjectsQueryData } from 'api/optProjects/optProjects.api'
import { SYSTEM_QUERY_KEY } from 'api/systems/systems.api'
import { UNITS_QUERY_KEY } from 'api/units/units.api'
import { queryClient } from 'helpers/queryClient'
import type { TFunction } from 'i18next'
import alertStore from 'store/alert/alert'
import { ALERT_KEYS } from 'store/alert/alert.constants'
import { SubtypeObject } from 'ui/components/NotificationCenter/NotificationCenter.helper'

// ============================================================================
// TYPES & CONSTANTS
// ============================================================================

export interface OptimizationEventData {
  organization: number
  opt_job_type: OptJobType
  system: number
  opt_model: number
  status: OptJobStatus
  created_by: number | null
  opt_project: number | undefined
  subtypes?: SubtypeObject[]
}

/**
 * Standardized context object passed to all optimization handlers
 * Provides consistent access to event data, user info, and utilities
 */
export interface OptimizationHandlerContext {
  data: OptimizationEventData
  user: User | undefined
  t: TFunction
}

/**
 * Configuration for each optimization type handler
 * Defines what actions to take for each status
 */
export interface OptimizationTypeConfig {
  onCommonInvalidate?: () => void
  /** Called when status is 'Finished' - handles data refresh */
  onFinishedRefresh?: () => Promise<void>
  /** Called when status is 'Finished' - handles invalidations */
  onFinishedInvalidate?: (ctx: OptimizationHandlerContext) => void
  /** Called when status is 'Finished' - shows success alerts */
  onFinishedAlert?: (ctx: OptimizationHandlerContext) => void
  /** Called when status is 'Failed' or 'Unsolvable' - shows error alerts */
  onErrorAlert?: (ctx: OptimizationHandlerContext) => void
}

export const alertProps = {
  anchorOrigin: { vertical: `top` as const, horizontal: `right` as const },
  autoHideDuration: 6000,
}

// ============================================================================
// DATA REFRESH FUNCTIONS
// ============================================================================
// These functions handle refreshing data from the server after optimizations complete
// Each includes a random delay to prevent thundering herd when multiple clients receive events

/**
 * Adds random delay (0-10s) to prevent thundering herd
 * when multiple clients receive events simultaneously
 */
async function addRandomDelay(): Promise<void> {
  const sleepTime = Math.floor(Math.random() * 10000)
  await new Promise((resolve) => setTimeout(resolve, sleepTime))
}

/**
 * Refreshes data for regular optimizations
 */
export async function refreshRegularOptimizationData(): Promise<void> {
  await addRandomDelay()
  queryClient.invalidateQueries({ queryKey: [GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY] })
  reFetchAllDatasetsOnOptimize('regular')
}

/**
 * Refreshes data for regular_wip optimizations
 */
export async function refreshRegularWipOptimizationData(): Promise<void> {
  await addRandomDelay()
  queryClient.invalidateQueries({ queryKey: [GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY] })
  reFetchAllDatasetsOnOptimize('regular_wip')
}

/**
 * Refreshes data for measvalues_calculations optimizations
 */
export async function refreshMeasvaluesCalculationsData(): Promise<void> {
  await addRandomDelay()
  queryClient.invalidateQueries({ queryKey: [GET_DIGITAL_TWIN_ACCUMULATOR_INFO_QUERY_KEY] })
  reFetchAllDatasetsOnOptimize('measvalues_calculations')
}

/**
 * Refreshes data for fuel_plan optimizations
 */
export async function refreshFuelPlanData(): Promise<void> {
  await addRandomDelay()
  reFetchAllDatasetsOnFuelPlan()
}

/**
 * Refreshes data for electricity_price_scenario optimizations
 */
export async function refreshElectricityPriceScenarioData(): Promise<void> {
  await addRandomDelay()
  reFetchAllDatasetsOnElectricityPriceScenario()
}

// ============================================================================
// QUERY INVALIDATION FUNCTIONS
// ============================================================================
// These functions handle invalidating React Query caches after optimizations

/**
 * Invalidates common opt project queries (called for all statuses)
 */
export function invalidateOptProjectCommonQueries(opt_project: number, type: OptJobType, system: number): void {
  queryClient.invalidateQueries({
    queryKey: [OPT_JOBS_QUERY_KEY, `latest`, type, 'Any', opt_project, system],
  })
}

/**
 * Invalidates opt project queries when optimization finishes successfully
 */
export function invalidateOptProjectFinishedQueries(opt_project: number, type: OptJobType, system: number): void {
  const sandboxQuery = getSandboxProjectUiConfigRefreshToken(opt_project)
  queryClient.invalidateQueries({
    queryKey: [OPT_JOBS_QUERY_KEY, `latest`, type, 'Finished', opt_project, system],
  })
  queryClient.invalidateQueries({ queryKey: [OPT_PROJECTS_QUERY_KEY] })
  queryClient.invalidateQueries({ queryKey: [sandboxQuery] })
}

/**
 * Updates opt project query data for non-finished statuses
 */
export function updateOptProjectStatus(opt_project: number, system: number, status: OptJobStatus): void {
  setOptProjectsQueryData({ projectId: opt_project, system, status })
}

/**
 * Invalidates queries after a regular optimization finishes successfully
 */
export function invalidateRegularOptimizationQueries(ctx: OptimizationHandlerContext): void {
  const { data } = ctx
  const { system, opt_model } = data

  queryClient.invalidateQueries({ queryKey: [SYSTEM_QUERY_KEY, system] })
  queryClient.invalidateQueries({ queryKey: [OPT_JOBS_QUERY_KEY, 'latest', 'regular', 'Finished', system] })
  queryClient.invalidateQueries({
    queryKey: [UNITS_QUERY_KEY, { belongs_to_opt_model: opt_model, unit_type: `Storage` }],
  })
  queryClient.invalidateQueries({ queryKey: [OPT_JOBS_QUERY_KEY, 'latest', 'regular', 2, 'Any', system] })
}

/**
 * Invalidates queries after a regular_wip optimization finishes successfully
 */
export function invalidateRegularWipOptimizationQueries(ctx: OptimizationHandlerContext): void {
  const { data } = ctx
  const { system, opt_model } = data

  queryClient.invalidateQueries({ queryKey: [SYSTEM_QUERY_KEY, system] })
  queryClient.invalidateQueries({ queryKey: [OPT_JOBS_QUERY_KEY, 'latest', 'regular_wip', 'Finished', system] })
  queryClient.invalidateQueries({
    queryKey: [UNITS_QUERY_KEY, { belongs_to_opt_model: opt_model, unit_type: `Storage` }],
  })
  queryClient.invalidateQueries({ queryKey: [OPT_JOBS_QUERY_KEY, 'latest', 'regular_wip', 2, 'Any', system] })
}

/**
 * Invalidates queries after a fuel_plan optimization finishes successfully
 */
export function invalidateFuelPlanOptimizationQueries(ctx: OptimizationHandlerContext): void {
  const { data } = ctx
  const { system, opt_model } = data

  queryClient.invalidateQueries({ queryKey: [SYSTEM_QUERY_KEY, system] })
  queryClient.invalidateQueries({ queryKey: [OPT_JOBS_QUERY_KEY, 'latest', 'fuel_plan', 'Finished', system] })
  queryClient.invalidateQueries({
    queryKey: [UNITS_QUERY_KEY, { belongs_to_opt_model: opt_model, unit_type: `Storage` }],
  })
  queryClient.invalidateQueries({ queryKey: [OPT_JOBS_QUERY_KEY, 'latest', 'fuel_plan', 2, 'Any', system] })
}

/**
 * Invalidates queries after an electricity_price_scenario optimization finishes successfully
 */
export function invalidateElectricityPriceScenarioOptimizationQueries(ctx: OptimizationHandlerContext): void {
  const { data } = ctx
  const { system, opt_model } = data

  queryClient.invalidateQueries({ queryKey: [SYSTEM_QUERY_KEY, system] })
  queryClient.invalidateQueries({
    queryKey: [OPT_JOBS_QUERY_KEY, 'latest', 'electricity_price_scenario', 'Finished', system],
  })
  queryClient.invalidateQueries({
    queryKey: [UNITS_QUERY_KEY, { belongs_to_opt_model: opt_model, unit_type: `Storage` }],
  })
  queryClient.invalidateQueries({
    queryKey: [OPT_JOBS_QUERY_KEY, 'latest', 'electricity_price_scenario', 2, 'Any', system],
  })
  queryClient.invalidateQueries({ queryKey: [ELPLAN_BID_MATRIX_REFRESH_EVENT_QUERY_KEY] })
}

/**
 * Invalidates queries for real-time optimizations
 */
export function invalidateRealTimeQueries(): void {
  queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_ON_REAL_TIME_OPTIMIZE] })
}

/**
 * Invalidates queries for follow-up optimizations
 */
export function invalidateFollowUpQueries(): void {
  queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_FOLLOW_UP] })
  queryClient.invalidateQueries({ queryKey: [DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS] })
}

// ============================================================================
// ALERT FUNCTIONS
// ============================================================================
// These functions show user notifications for optimization events
// Each is specific to an optimization type and status for clarity

/**
 * Shows success alert for regular optimization
 */
export function alertRegularOptimizationSuccess(ctx: OptimizationHandlerContext): void {
  const { data, user, t } = ctx

  if (data.subtypes) {
    const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
    alertStore.success(
      t(`A new production plan optimization finished for subtypes: {{subtypeNames}}.`, { subtypeNames }),
      alertProps,
      ALERT_KEYS.productionPlanOptimizationFinished
    )
  }

  if (data.created_by === null) {
    alertStore.success(
      t(`An automatic production plan optimization finished just now.`),
      alertProps,
      ALERT_KEYS.productionPlanOptimizationFinished
    )
  } else if (user && data.created_by !== user.id) {
    alertStore.success(
      t(`A new production plan optimization just finished! The new result is presented in 'Production plan'.`),
      alertProps,
      ALERT_KEYS.productionPlanOptimizationFinished
    )
  } else {
    alertStore.success(
      t(`A new production plan optimization was successful.`),
      alertProps,
      ALERT_KEYS.productionPlanOptimizationFinished
    )
  }
}

/**
 * Shows error alert for regular optimization failure
 */
export function alertRegularOptimizationError(ctx: OptimizationHandlerContext): void {
  const { data, t } = ctx

  if (FAILED_OPT_JOB_STATUSES.has(data.status)) {
    if (data.subtypes) {
      const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
      alertStore.error(
        t(`A production plan optimization failed for subtypes: {{subtypeNames}}.`, { subtypeNames }),
        alertProps,
        ALERT_KEYS.productionPlanOptimizationFailed
      )
    }
    alertStore.error(
      t(`A production plan optimization failed.`),
      alertProps,
      ALERT_KEYS.productionPlanOptimizationFailed
    )
  }
}

/**
 * Shows success alert for published production plan (regular with use_draftproductionplan feature flag)
 */
export function alertPublishedProductionPlanSuccess(ctx: OptimizationHandlerContext): void {
  const { data, user, t } = ctx

  if (data.subtypes) {
    const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
    alertStore.success(
      t(`A new production plan was published for subtypes: {{subtypeNames}}.`, { subtypeNames }),
      alertProps,
      ALERT_KEYS.productionPlanPublished
    )
  }

  if (data.created_by === null) {
    alertStore.success(t(`An automatic production plan was published`), alertProps, ALERT_KEYS.productionPlanPublished)
  } else if (user && data.created_by !== user.id) {
    alertStore.success(
      t(`A new production plan was published. The new result is presented in 'Production plan'.`),
      alertProps,
      ALERT_KEYS.productionPlanPublished
    )
  } else {
    alertStore.success(t(`A new production plan was published.`), alertProps, ALERT_KEYS.productionPlanPublished)
  }
}

/**
 * Shows error alert for published production plan failure
 */
export function alertPublishedProductionPlanError(ctx: OptimizationHandlerContext): void {
  const { data, t } = ctx

  if (FAILED_OPT_JOB_STATUSES.has(data.status)) {
    if (data.subtypes) {
      const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
      alertStore.error(
        t(`Publishing production plan failed for subtypes: {{subtypeNames}}.`, { subtypeNames }),
        alertProps,
        ALERT_KEYS.productionPlanPublishFailed
      )
    }
    alertStore.error(t(`Publishing production plan failed`), alertProps, ALERT_KEYS.productionPlanPublishFailed)
  }
}

/**
 * Shows success alert for regular_wip optimization
 */
export function alertRegularWipOptimizationSuccess(ctx: OptimizationHandlerContext): void {
  const { data, user, t } = ctx

  if (data.subtypes) {
    const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
    alertStore.success(
      t(
        `A draft production plan optimization finished for subtypes: {{subtypeNames}}. The new result is presented in 'Draft production plan'.`,
        { subtypeNames }
      ),
      alertProps
    )
  }

  if (data.created_by === null) {
    alertStore.success(t(`An automatic draft production plan finished just now.`), alertProps)
  } else if (user && data.created_by !== user.id) {
    alertStore.success(
      t(`A draft production plan optimization just finished! The new result is presented in 'Draft production plan'.`),
      alertProps
    )
  } else {
    alertStore.success(t(`A draft production plan optimization was successful.`), alertProps)
  }
}

/**
 * Shows error alert for regular_wip optimization failure
 */
export function alertRegularWipOptimizationError(ctx: OptimizationHandlerContext): void {
  const { data, t } = ctx

  if (FAILED_OPT_JOB_STATUSES.has(data.status)) {
    if (data.subtypes) {
      const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
      alertStore.error(
        t(`A draft production plan optimization failed for subtypes: {{subtypeNames}}`, { subtypeNames }),
        alertProps,
        ALERT_KEYS.draftProductionPlanOptimizationFailed
      )
    }
    alertStore.error(
      t(`A draft production plan optimization failed`),
      alertProps,
      ALERT_KEYS.draftProductionPlanOptimizationFailed
    )
  }
}

/**
 * Shows success alert for fuel_plan optimization
 */
export function alertFuelPlanOptimizationSuccess(ctx: OptimizationHandlerContext): void {
  const { data, user, t } = ctx

  if (data.subtypes) {
    const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
    alertStore.success(
      t(
        `A new fuel plan optimization finished for subtypes: {{subtypeNames}}. The new result is presented in 'Fuel plan'.`,
        {
          subtypeNames,
        }
      ),
      alertProps
    )
  }

  if (data.created_by === null) {
    alertStore.success(t(`An automatic optimization finished just now`), alertProps)
  } else if (user && data.created_by !== user.id) {
    alertStore.success(
      t(`A new fuel plan optimization just finished! The new result is presented in 'Fuel plan'.`),
      alertProps
    )
  } else {
    alertStore.success(t(`A fuel plan optimization was successful.`), alertProps)
  }
}

/**
 * Shows error alert for fuel_plan optimization failure
 */
export function alertFuelPlanOptimizationError(ctx: OptimizationHandlerContext): void {
  const { data, t } = ctx

  if (FAILED_OPT_JOB_STATUSES.has(data.status)) {
    if (data.subtypes) {
      const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
      alertStore.error(
        t(`A fuel plan optimization failed for subtypes: {{subtypeNames}}`, { subtypeNames }),
        alertProps
      )
    }
    alertStore.error(t(`A fuel plan optimization failed`), alertProps)
  }
}

/**
 * Shows success alert for electricity_price_scenario optimization
 */
export function alertElectricityPriceScenarioOptimizationSuccess(ctx: OptimizationHandlerContext): void {
  const { data, user, t } = ctx

  if (data.subtypes) {
    const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
    alertStore.success(
      t(`Electricity price scenario optimization finished for subtypes: {{subtypeNames}}`, { subtypeNames }),
      alertProps
    )
  }

  if (data.created_by === null) {
    alertStore.success(t(`An automatic electricity price scenario optimization finished just now`), alertProps)
  } else if (user && data.created_by !== user.id) {
    alertStore.success(t(`Electricity price scenario optimization finished.`), alertProps)
  } else {
    alertStore.success(t(`An electricity price scenario optimization was successful.`), alertProps)
  }
}

/**
 * Shows error alert for electricity_price_scenario optimization failure
 */
export function alertElectricityPriceScenarioOptimizationError(ctx: OptimizationHandlerContext): void {
  const { data, t } = ctx

  if (FAILED_OPT_JOB_STATUSES.has(data.status)) {
    if (data.subtypes) {
      const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
      alertStore.error(
        t(`An electricity price scenario optimization failed for subtypes: {{subtypeNames}}.`, { subtypeNames }),
        alertProps
      )
    }
    alertStore.error(t(`An electricity price scenario optimization failed.`), alertProps)
  }
}

/**
 * Shows success alert for sandbox optimization
 * Only shows alert to the user who created the optimization
 */
export function alertSandboxSuccess(ctx: OptimizationHandlerContext): void {
  const { data, user, t } = ctx

  // Only show alert to the user who created the optimization
  if (data.created_by !== user?.id) return

  alertStore.success(t(`Sandbox optimization successful.`), alertProps)
}

/**
 * Shows error alert for sandbox optimization failure
 * Only shows alert to the user who created the optimization
 */
export function alertSandboxError(ctx: OptimizationHandlerContext): void {
  const { data, user, t } = ctx

  // Only show alert to the user who created the optimization
  if (data.created_by !== user?.id) return

  if (data.status === 'Failed') {
    alertStore.error(t(`Sandbox optimization failed.`), alertProps)
  } else if (data.status === 'Unsolvable') {
    alertStore.error(
      t(`No optimal solution could be found in sandbox optimization. Check your settings and try again.`),
      alertProps
    )
  }
}

/**
 * Shows success alert for follow-up optimization
 */
export function alertFollowUpSuccess(ctx: OptimizationHandlerContext): void {
  const { data, t } = ctx
  if (data.subtypes) {
    const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
    alertStore.success(
      t(`A follow-up optimization finished for subtypes: {{subtypeNames}}.`, { subtypeNames }),
      alertProps
    )
  }

  alertStore.success(t(`A follow-up optimization was successful.`), alertProps)
}

/**
 * Shows error alert for follow-up optimization failure
 */
export function alertFollowUpError(ctx: OptimizationHandlerContext): void {
  const { data, t } = ctx

  if (FAILED_OPT_JOB_STATUSES.has(data.status)) {
    if (data.subtypes) {
      const subtypeNames = data.subtypes.map((subtype) => subtype.display_name).join(', ')
      alertStore.error(
        t(`A follow-up optimization failed for subtypes: {{subtypeNames}}.`, { subtypeNames }),
        alertProps
      )
    }
    alertStore.error(t(`A follow-up optimization failed.`), alertProps)
  }
}

function commonInvalidateRegular(): void {
  queryClient.invalidateQueries({ queryKey: [OPT_JOBS_AND_QUEUE_ITEMS_STATUS_QUERY_KEY, 'regular'] })
}

function commonInvalidateRegularWip(): void {
  queryClient.invalidateQueries({ queryKey: [OPT_JOBS_AND_QUEUE_ITEMS_STATUS_QUERY_KEY, 'regular_wip'] })
}

// ============================================================================
// HANDLER REGISTRY
// ============================================================================
// Maps optimization types to their specific handlers
// This is the strategy pattern implementation

/**
 * Handler configuration for regular optimizations
 * with the use_draftproductionplan feature flag enabled (published production plan)
 */
export const publishedRegularConfig: OptimizationTypeConfig = {
  onCommonInvalidate: commonInvalidateRegular,
  onFinishedRefresh: refreshRegularOptimizationData,
  onFinishedInvalidate: invalidateRegularOptimizationQueries,
  onFinishedAlert: alertPublishedProductionPlanSuccess, // Different alert function for published
  onErrorAlert: alertPublishedProductionPlanError, // Different alert function for published
}

/**
 * Handler configuration for regular optimizations
 * without the use_draftproductionplan feature flag
 */
export const regularConfig: OptimizationTypeConfig = {
  onCommonInvalidate: commonInvalidateRegular,
  onFinishedRefresh: refreshRegularOptimizationData,
  onFinishedInvalidate: invalidateRegularOptimizationQueries,
  onFinishedAlert: alertRegularOptimizationSuccess, // Different alert function for non-published
  onErrorAlert: alertRegularOptimizationError, // Different alert function for non-published
}

/**
 * Handler configuration for regular_wip optimizations (requires permission)
 */
export const regularWipConfig: OptimizationTypeConfig = {
  onCommonInvalidate: commonInvalidateRegularWip,
  onFinishedRefresh: refreshRegularWipOptimizationData,
  onFinishedInvalidate: invalidateRegularWipOptimizationQueries,
  onFinishedAlert: alertRegularWipOptimizationSuccess,
  onErrorAlert: alertRegularWipOptimizationError,
}

/**
 * Handler configuration for fuel_plan optimizations
 */
export const fuelPlanConfig: OptimizationTypeConfig = {
  onFinishedRefresh: refreshFuelPlanData,
  onFinishedInvalidate: invalidateFuelPlanOptimizationQueries,
  onFinishedAlert: alertFuelPlanOptimizationSuccess,
  onErrorAlert: alertFuelPlanOptimizationError,
}

/**
 * Handler configuration for electricity_price_scenario optimizations
 */
export const electricityPriceScenarioConfig: OptimizationTypeConfig = {
  onFinishedRefresh: refreshElectricityPriceScenarioData,
  onFinishedInvalidate: invalidateElectricityPriceScenarioOptimizationQueries,
  onFinishedAlert: alertElectricityPriceScenarioOptimizationSuccess,
  onErrorAlert: alertElectricityPriceScenarioOptimizationError,
}

/**
 * Handler configuration for measvalues_calculations optimizations
 * Only refreshes data on finish, no alerts
 */
export const measvaluesCalculationsConfig: OptimizationTypeConfig = {
  onFinishedRefresh: refreshMeasvaluesCalculationsData,
}

/**
 * Handler configuration for sandbox optimizations
 * Only shows alerts to the user who created the optimization
 */
export const sandboxConfig: OptimizationTypeConfig = {
  onFinishedAlert: alertSandboxSuccess,
  onErrorAlert: alertSandboxError,
}

/**
 * Handler configuration for real_time and real_time_measvalues_calculations optimizations
 */
export const realTimeConfig: OptimizationTypeConfig = {
  onFinishedInvalidate: invalidateRealTimeQueries,
}

/**
 * Handler configuration for followup_without_deviations optimizations
 */
export const followUpConfig: OptimizationTypeConfig = {
  onFinishedInvalidate: invalidateFollowUpQueries,
  onFinishedAlert: alertFollowUpSuccess,
  onErrorAlert: alertFollowUpError,
}
